import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { concatMap, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { JobState, JobStatus, RingCentralCallQueueReportEntry } from '../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { RingCentralCallQueuesPollingContext } from './ring-central-call-queues-polling.context';
import { convertScreamingSnakeCaseToTitleCase } from '../../../shared/utils';

enum RoutingMethodEnum {
  FIXED_ORDER = 'FixedOrder',
  SIMULTANEOUS = 'Simultaneous',
  ROTATING = 'Rotating',
  UNKNOWN = 'Unknown',
}

type RoutingMethodTkey = `tkey;migrations.ring_central.call_queues.routing_method.${string}`;
type ErrorHandlingActionTkey = `tkey;migrations.ring_central.call_queues.error_handling.action.${string}`;

enum OverflowActionEnum {
  VOICEMAIL = 'Voicemail',
  ANNOUNCEMENT = 'Announcement',
  TRANSFER_TO_EXTENSION = 'Transfer To Extension',
  UNCONDITIONAL_FORWARDING = 'Unconditional Forwarding',
}

enum TimeoutActionEnum {
  TRANSFER_TO_EXTENSION = 'Transfer To Extension',
  UNCONDITIONAL_FORWARDING = 'Unconditional Forwarding',
  VOICEMAIL = 'Voicemail',
}

enum NoAgentsActionEnum {
  WAIT_PRIMARY_MEMBERS = 'Wait Primary Members',
  WAIT_PRIMARY_AND_OVERFLOW_MEMBERS = 'Wait Primary And Overflow Members',
  VOICEMAIL = 'Voicemail',
  TRANSFER_TO_EXTENSION = 'Transfer To Extension',
  UNCONDITIONAL_FORWARDING = 'Unconditional Forwarding',
}

@Injectable()
export class RingCentralCallQueuesContext implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _pollingContext = inject(RingCentralCallQueuesPollingContext);

  _stateSource = new ReplaySubject<RingCentralCallQueueReportEntry[]>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params['tenantId']}/exports/ring-central/${this._route.snapshot.params['id']}/call-queues`;

  private _overflowActionTkeys: Record<OverflowActionEnum, ErrorHandlingActionTkey> = {
    [OverflowActionEnum.VOICEMAIL]: 'tkey;migrations.ring_central.call_queues.error_handling.action.voicemail',
    [OverflowActionEnum.ANNOUNCEMENT]: 'tkey;migrations.ring_central.call_queues.error_handling.action.announcement',
    [OverflowActionEnum.TRANSFER_TO_EXTENSION]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.transfer_to_extension',
    [OverflowActionEnum.UNCONDITIONAL_FORWARDING]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.unconditional_forwarding',
  };

  private _timeoutActionTkeys: Record<TimeoutActionEnum, ErrorHandlingActionTkey> = {
    [TimeoutActionEnum.TRANSFER_TO_EXTENSION]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.transfer_to_extension',
    [TimeoutActionEnum.UNCONDITIONAL_FORWARDING]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.unconditional_forwarding',
    [TimeoutActionEnum.VOICEMAIL]: 'tkey;migrations.ring_central.call_queues.error_handling.action.voicemail',
  };

  private _noAgentsActionTkeys: Record<NoAgentsActionEnum, ErrorHandlingActionTkey> = {
    [NoAgentsActionEnum.WAIT_PRIMARY_MEMBERS]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.wait_primary_members',
    [NoAgentsActionEnum.WAIT_PRIMARY_AND_OVERFLOW_MEMBERS]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.wait_primary_and_overflow_members',
    [NoAgentsActionEnum.VOICEMAIL]: 'tkey;migrations.ring_central.call_queues.error_handling.action.voicemail',
    [NoAgentsActionEnum.TRANSFER_TO_EXTENSION]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.transfer_to_extension',
    [NoAgentsActionEnum.UNCONDITIONAL_FORWARDING]:
      'tkey;migrations.ring_central.call_queues.error_handling.action.unconditional_forwarding',
  };

  private _routingMethodTkeys: Record<RoutingMethodEnum, RoutingMethodTkey> = {
    [RoutingMethodEnum.FIXED_ORDER]: 'tkey;migrations.ring_central.call_queues.routing_method.fixedorder',
    [RoutingMethodEnum.SIMULTANEOUS]: 'tkey;migrations.ring_central.call_queues.routing_method.simultaneous',
    [RoutingMethodEnum.ROTATING]: 'tkey;migrations.ring_central.call_queues.routing_method.rotating',
    [RoutingMethodEnum.UNKNOWN]: 'tkey;migrations.ring_central.call_queues.routing_method.unknown',
  };

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._pollingContext.stopPolling();
  }

  getOverflowActionTkey(action: string): ErrorHandlingActionTkey {
    return this._overflowActionTkeys[
      (Object.values(OverflowActionEnum) as OverflowActionEnum[]).find(
        (value) => value === convertScreamingSnakeCaseToTitleCase(action),
      )
    ];
  }

  getTimeoutActionTkey(action: string): ErrorHandlingActionTkey {
    return this._timeoutActionTkeys[
      (Object.values(TimeoutActionEnum) as TimeoutActionEnum[]).find(
        (value) => value === convertScreamingSnakeCaseToTitleCase(action),
      )
    ];
  }

  getNoAgentsActionTkey(action: string): ErrorHandlingActionTkey {
    return this._noAgentsActionTkeys[
      (Object.values(NoAgentsActionEnum) as NoAgentsActionEnum[]).find(
        (value) => value === convertScreamingSnakeCaseToTitleCase(action),
      )
    ];
  }

  getRoutingMethodTkey(routingMethod: string): RoutingMethodTkey {
    return this._routingMethodTkeys[
      (Object.values(RoutingMethodEnum) as RoutingMethodEnum[]).find((value) => value === routingMethod) ??
        RoutingMethodEnum.UNKNOWN
    ];
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  private _init(): Observable<void> {
    this._pollingContext.startPolling();

    return this._pollingContext.state$.pipe(
      tap((status) => this._jobStateSource.next(status)),
      filter((status) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._pollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of(null);
      }),
      filter((callQueues) => !!callQueues),
      map((callQueues: RingCentralCallQueueReportEntry[]) => {
        this._stateSource.next(callQueues);
      }),
      take(1),
    );
  }

  private _get(): Observable<RingCentralCallQueueReportEntry[]> {
    return this._http.get<RingCentralCallQueueReportEntry[]>(this._baseUrl);
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/refresh`, null);
  }
}
